import { useState, useEffect } from 'react';
import {
  UserCircleIcon,
  MapPinIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import orgChartService from '../../services/orgChartService';
import { UserService } from '@/services';
import UserSearchCombobox from './UserSearchCombobox';

/**
 * DEFAULT_NOTIFY_CHANNELS — mirrors the consequence-type notify editor and the
 * server-side notifyTargetsResolver. Email + in-app on by default; telegram/sms
 * are placeholders (disabled in the UI) until those channels ship.
 */
export const DEFAULT_NOTIFY_CHANNELS = { email: true, inApp: true, telegram: false, sms: false };

/**
 * NotifyTargetsEditor — reusable recipient picker for the notifyTargets pattern.
 *
 * A "notify target" is either a named staff member ({ type:'user', value:uid })
 * or an org-chart position ({ type:'position', value:label }). The server-side
 * `notifyTargetsResolver.js` resolves both to concrete recipients, so anything
 * built with this editor delivers through the same path as consequence notifies.
 *
 * Controlled component: pass the current `value` array and an `onChange` handler.
 * This is the same UI used in ConsequenceTypeExpandedContent — extracted so the
 * weekly discipline brief (and future features) reuse it without duplication.
 *
 * @param {object}   props
 * @param {string}   props.schoolId   - school whose org chart supplies positions
 * @param {Array}    props.value      - current notify targets
 * @param {Function} props.onChange   - (nextTargets) => void
 * @param {string}   [props.addStaffLabel]
 * @param {string}   [props.emptyHint]
 */
export default function NotifyTargetsEditor({
  schoolId,
  value = [],
  onChange,
  addStaffLabel = 'Add staff member',
  emptyHint = 'No one configured yet. Add staff or a position above.',
}) {
  const [orgPositions, setOrgPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [usersById, setUsersById] = useState({});

  const targets = Array.isArray(value) ? value : [];

  // Load org chart positions for the position picker.
  useEffect(() => {
    if (!schoolId) return;
    orgChartService.getOrgCharts(schoolId)
      .then(charts => {
        const allPositions = orgChartService.getAllPositions(charts);
        const labels = [...new Set(allPositions.map(p => p.label).filter(Boolean))].sort();
        setOrgPositions(labels);
      })
      .catch(err => console.warn('[NotifyTargetsEditor] Could not load org positions:', err));
  }, [schoolId]);

  // Resolve named-user targets to name + email so each row is identifiable —
  // stored targets only carry a uid (and sometimes a stale label), which on its
  // own can't tell two people apart.
  useEffect(() => {
    if (!schoolId) return;
    UserService.getAllUsers(false, schoolId)
      .then(users => {
        const map = {};
        for (const u of users) map[u.id] = u;
        setUsersById(map);
      })
      .catch(err => console.warn('[NotifyTargetsEditor] Could not load users:', err));
  }, [schoolId]);

  // Resolve a target to { name, sublabel } for display.
  const describeTarget = (target) => {
    if (target.type === 'user') {
      const u = usersById[target.value];
      const name = u?.displayName || u?.name || target.label || u?.email || 'Unknown staff member';
      const email = u?.email || null;
      // Only show the email as a subtitle when it adds information.
      return { name, sublabel: email && email !== name ? email : null };
    }
    return { name: target.label || target.value, sublabel: 'Org chart position' };
  };

  const setChannel = (idx, channel, checked) => {
    const next = targets.map((t, i) =>
      i === idx ? { ...t, channels: { ...t.channels, [channel]: checked } } : t
    );
    onChange(next);
  };

  return (
    <div>
      {/* Add staff member */}
      <div className="mb-3">
        <p className="text-xs text-base-content/60 mb-1">{addStaffLabel}</p>
        <UserSearchCombobox
          userTypes={['staff']}
          excludeUserIds={targets.filter(t => t.type === 'user').map(t => t.value)}
          placeholder="Search staff..."
          onSelect={(user) => {
            onChange([
              ...targets,
              {
                type: 'user',
                value: user.id,
                label: user.displayName || user.email || user.id,
                channels: { ...DEFAULT_NOTIFY_CHANNELS },
                scope: null,
              },
            ]);
          }}
        />
      </div>

      {/* Add org chart position */}
      {orgPositions.length > 0 && (
        <div className="mb-4">
          <p className="text-xs text-base-content/60 mb-1">Add org chart position</p>
          <select
            className="select select-bordered select-sm w-full"
            value={selectedPosition}
            onChange={(e) => {
              const label = e.target.value;
              if (!label) return;
              onChange([
                ...targets,
                {
                  type: 'position',
                  value: label,
                  label,
                  channels: { ...DEFAULT_NOTIFY_CHANNELS },
                  scope: null,
                },
              ]);
              setSelectedPosition('');
            }}
          >
            <option value="">Select a position…</option>
            {orgPositions.map(label => (
              <option
                key={label}
                value={label}
                disabled={targets.some(t => t.type === 'position' && t.value === label)}
              >
                {label}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* Target list */}
      {targets.length > 0 ? (
        <div className="space-y-2">
          {targets.map((target, idx) => {
            const { name, sublabel } = describeTarget(target);
            return (
            <div key={idx} className="p-2.5 bg-base-200 rounded-lg">
              {/* Identity line — name, type, and remove (always visible) */}
              <div className="flex items-center gap-2">
                {target.type === 'user' ? (
                  <UserCircleIcon className="w-5 h-5 text-primary flex-shrink-0" />
                ) : (
                  <MapPinIcon className="w-5 h-5 text-secondary flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{name}</div>
                  {sublabel && (
                    <div className="text-xs text-base-content/50 truncate">{sublabel}</div>
                  )}
                </div>
                <span className={`badge badge-xs flex-shrink-0 ${target.type === 'user' ? 'badge-primary' : 'badge-secondary'}`}>
                  {target.type === 'user' ? 'User' : 'Position'}
                </span>
                <button
                  onClick={() => onChange(targets.filter((_, i) => i !== idx))}
                  className="btn btn-ghost btn-xs btn-square text-error flex-shrink-0"
                  title="Remove"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              </div>

              {/* Channel line — wraps so it never pushes the row off-panel */}
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-2 pl-7">
                <label className="flex items-center gap-1 text-xs" title="Email">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.email !== false}
                    onChange={(e) => setChannel(idx, 'email', e.target.checked)}
                  />
                  <span>Email</span>
                </label>
                <label className="flex items-center gap-1 text-xs" title="In-app">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.inApp !== false}
                    onChange={(e) => setChannel(idx, 'inApp', e.target.checked)}
                  />
                  <span>App</span>
                </label>
                <label className="flex items-center gap-1 text-xs opacity-40" title="Coming soon">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.telegram === true}
                    disabled
                    onChange={() => {}}
                  />
                  <span>Telegram</span>
                </label>
                <label className="flex items-center gap-1 text-xs opacity-40" title="Coming soon">
                  <input
                    type="checkbox"
                    className="toggle toggle-xs"
                    checked={target.channels?.sms === true}
                    disabled
                    onChange={() => {}}
                  />
                  <span>SMS</span>
                </label>
              </div>
            </div>
            );
          })}
        </div>
      ) : (
        <p className="text-xs text-base-content/40">{emptyHint}</p>
      )}
    </div>
  );
}
